/**
 * Convert a picked image file to WebP, resizing so the longest edge
 * is at most `maxEdge` (default 2048px). Keeps file size small while
 * preserving enough resolution for hero/product imagery.
 *
 * If the original file is already smaller than the produced blob,
 * we return the original — no point losing quality on tiny files.
 *
 * Browser support: relies on canvas + createImageBitmap + canvas.toBlob
 * with `image/webp`. All modern browsers support this; Safari got
 * createImageBitmap in 16.4, WebP encode in 14.
 */
const DEFAULT_MAX_EDGE = 2048
const DEFAULT_QUALITY = 0.85

export async function optimizeImage(
  file: File,
  options: { maxEdge?: number; quality?: number } = {},
): Promise<Blob> {
  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE
  const quality = options.quality ?? DEFAULT_QUALITY

  // Decode the source image
  const bitmap = await createImageBitmap(file)

  // Compute target dimensions preserving aspect ratio
  const { width: srcW, height: srcH } = bitmap
  let targetW = srcW
  let targetH = srcH
  if (Math.max(srcW, srcH) > maxEdge) {
    const scale = maxEdge / Math.max(srcW, srcH)
    targetW = Math.round(srcW * scale)
    targetH = Math.round(srcH * scale)
  }

  // Draw onto an offscreen canvas at the target size, then encode
  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Failed to acquire 2D canvas context')
  ctx.drawImage(bitmap, 0, 0, targetW, targetH)
  bitmap.close()

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/webp', quality)
  })

  if (!blob) throw new Error('Failed to encode WebP')

  // If our optimized version is bigger than the original, keep the
  // original — likely a tiny file or one already compressed well.
  if (blob.size >= file.size) return file
  return blob
}

/** Convert a Blob back into a File so FormData has a filename. */
export function blobToFile(blob: Blob, filename: string, type = 'image/webp'): File {
  return new File([blob], filename, { type })
}